import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, filter, map, shareReplay } from 'rxjs';
import { environment } from '../../../environments/environment';
import { postWithUploadProgress, UploadProgressEvent } from '../utils/http-upload-progress';
import {
  EventDetailApiImage,
  EventDetailApiRegistration,
  EventDetailApiSchedule,
  EventDetailApiTicket,
} from '../models/event.models';

export type { UploadProgressEvent };

/** Resposta de POST /private/app/member/change_personal_data — `content` só traz métricas de perfil. */
export interface ChangePersonalDataContent {
  completed_profile_flag: boolean;
  profile_completion_percent: number;
}

export interface ChangePersonalDataResponse {
  status: string;
  message: string;
  content: ChangePersonalDataContent;
}

/** Resposta de POST /private/app/member/change_company_logo — inclui URL do logo e métricas de perfil. */
export interface ChangeCompanyLogoContent extends ChangePersonalDataContent {
  logo: string;
}

export interface ChangeCompanyLogoResponse {
  status: string;
  message: string;
  content: ChangeCompanyLogoContent;
}

/** Body de POST change_personal_data — campos opcionais; omitir mantém valor no servidor. */
export interface MemberPersonalDataUpdatePayload {
  name_complete?: string;
  cpf?: string;
  phone?: string;
  email?: string;
  instagram?: string;
  birth_date?: string;
  gender?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  show_phone?: boolean;
  show_email?: boolean;
  professional_activity?: string;
}

// --- Member Info (GET /private/app/member/info) ---

/** Interesse do membro (GET info) — `id` quando vier do catálogo / vínculo. */
export interface MemberInterest {
  id?: number;
  name: string;
  description: string;
  type: string;
}

/** Item do catálogo global em GET /private/app/member/get_interests.json */
export interface InterestCatalogItem {
  id: number;
  name: string;
  description: string | null;
  type: string | null;
  created_at: string;
  updated_at: string;
}

/** Chaves = `interest_type` no banco ou `sem_tipo`. */
export type InterestsByType = Record<string, InterestCatalogItem[]>;

export interface GetInterestsResponse {
  status: string;
  message: string;
  content?: {
    interests_by_type: InterestsByType;
  };
  type?: string;
  code?: number;
}

export interface CompanyImage {
  id?: number;
  /** GET member/info costuma expor `url`; detalhe de empresa / upload podem usar `image_url`. */
  url?: string | { url?: string | null } | null;
  image_url?: string | { url?: string | null } | null;
  thumb_url?: string | { url?: string | null } | null;
  position: number;
  description?: string | null;
}

export interface CompanyMember {
  member_id?: string;
  public_id?: string;
  member_name: string;
  /** API pode enviar URL string ou `{ url }`. */
  member_avatar: string | { url: string } | null;
  position: string;
}

/** Body de PUT /private/app/member/change_member_position.json */
export interface ChangeMemberPositionPayload {
  member_id: string;
  position: string;
}

export interface ChangeMemberPositionContent {
  member_id: string;
  member_name: string;
  position: string;
}

export interface ChangeMemberPositionResponse {
  status: string;
  message: string;
  content: ChangeMemberPositionContent;
}

export interface CompanyCategory {
  /** PK em `categories`, quando o GET da empresa envia. */
  id?: number;
  name: string;
  description: string;
  label_category?: string | null;
}

export interface MemberCompany {
  id: string;
  logo: string | null;
  cnpj: string;
  legal_name: string;
  trade_name: string;
  /** HTML permitido (negrito, itálico, fonte, alinhamento). */
  description?: string | null;
  company_instagram?: string | null;
  website?: string | null;
  address: string;
  address_number?: string | null;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  phone: string;
  created_at: string;
  updated_at: string;
  /** Galeria (GET /member/info — `images[].url`). */
  images?: CompanyImage[];
  members: CompanyMember[];
  categories: CompanyCategory[];
  /** Faixas gravadas em `companies` (POST change_professional_data). */
  annual_revenue_string?: string | null;
  number_employees_string?: string | null;
  annual_revenue_flag?: boolean | null;
  number_employees_flag?: boolean | null;
}

export interface MemberProfile {
  id: string;
  name: string;
  name_complete?: string;
  email: string;
  cpf?: string;
  phone?: string;
  show_phone?: boolean;
  show_email?: boolean;
  professional_activity?: string;
  average_annual_revenue?: number;
  employees_count?: number;
  industry_sector?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  birth_date?: string;
  gender?: string;
  /** API pode retornar string ou `{ url }` (mesmo formato do login em `member_data`). */
  avatar?: string | { url: string } | null;
  instagram?: string;
  description?: string;
  goal_redal?: string;
  event_theme_speaker_suggestions?: string | null;
  economic_scenario_rating?: number | null;
  business_news_reading_frequency?: BusinessNewsReadingFrequencySlug | null;
  created_at?: string;
  interests?: MemberInterest[];
  content_preferences?: MemberContentPreference[];
  networking_objectives?: MemberNetworkingObjective[];
  training_information_areas?: MemberTrainingInformationArea[];
  event_participation_types?: MemberEventParticipationType[];
  companies?: MemberCompany[];
  profile_completion_percent?: number;
  completed_profile_flag?: boolean;
}

export interface MemberInfoResponse {
  status: string;
  message: string;
  content: MemberProfile;
}

/** Campos editáveis da empresa (sem relações aninhadas). Logo via POST change_company_logo (multipart). */
export type MemberCompanyUpdatePayload = Partial<
  Pick<
    MemberCompany,
    | 'legal_name'
    | 'trade_name'
    | 'description'
    | 'company_instagram'
    | 'website'
    | 'cnpj'
    | 'phone'
    | 'address'
    | 'address_number'
    | 'city'
    | 'state'
    | 'zip_code'
    | 'country'
  >
>;

/**
 * Body de POST change_company_date — `company_id` é o public_id; demais campos opcionais (omitir mantém no servidor).
 */
export type MemberChangeCompanyDataPayload = { company_id: string } & MemberCompanyUpdatePayload;

/**
 * Body de POST change_professional_data — `company_id` obrigatório; demais opcionais.
 * `company_categories` substitui o conjunto inteiro na empresa (omitir = não altera categorias).
 */
export interface ChangeProfessionalDataPayload {
  company_id: string;
  company_categories?: number[];
  annual_revenue_string?: string;
  number_employees_string?: string;
  label_category?: string;
  annual_revenue_flag?: boolean;
  number_employees_flag?: boolean;
}

/** Objetivos na REDAL (objetivo + sobre mim). */
export type MemberObjectivesUpdatePayload = Partial<
  Pick<MemberProfile, 'goal_redal' | 'description'>
>;

/** Conteúdo de GET /private/app/member/event_theme_speaker_suggestions.json */
export interface EventThemeSpeakerSuggestionsGetContent {
  event_theme_speaker_suggestions: string | null;
}

export interface EventThemeSpeakerSuggestionsGetResponse {
  status: string;
  message: string;
  content: EventThemeSpeakerSuggestionsGetContent;
}

/** Conteúdo de PUT /private/app/member/event_theme_speaker_suggestions.json */
export interface EventThemeSpeakerSuggestionsPutContent extends EventThemeSpeakerSuggestionsGetContent {
  completed_profile_flag: boolean;
  profile_completion_percent: number;
}

export interface EventThemeSpeakerSuggestionsPutResponse {
  status: string;
  message: string;
  content: EventThemeSpeakerSuggestionsPutContent;
}

/** Body de PUT /private/app/member/event_theme_speaker_suggestions.json */
export interface EventThemeSpeakerSuggestionsPutPayload {
  event_theme_speaker_suggestions: string;
}

/** Conteúdo de GET /private/app/member/economic_scenario_rating.json */
export interface EconomicScenarioRatingGetContent {
  economic_scenario_rating: number | null;
}

export interface EconomicScenarioRatingGetResponse {
  status: string;
  message: string;
  content: EconomicScenarioRatingGetContent;
}

/** Conteúdo de PUT /private/app/member/economic_scenario_rating.json */
export interface EconomicScenarioRatingPutContent extends EconomicScenarioRatingGetContent {
  completed_profile_flag: boolean;
  profile_completion_percent: number;
}

export interface EconomicScenarioRatingPutResponse {
  status: string;
  message: string;
  content: EconomicScenarioRatingPutContent;
}

/** Body de PUT /private/app/member/economic_scenario_rating.json */
export interface EconomicScenarioRatingPutPayload {
  economic_scenario_rating: number;
}

export type BusinessNewsReadingFrequencySlug =
  | 'daily'
  | 'weekly'
  | 'biweekly'
  | 'monthly'
  | 'rarely';

/** Conteúdo de GET /private/app/member/business_news_reading_frequency.json */
export interface BusinessNewsReadingFrequencyGetContent {
  business_news_reading_frequency: BusinessNewsReadingFrequencySlug | null;
}

export interface BusinessNewsReadingFrequencyGetResponse {
  status: string;
  message: string;
  content: BusinessNewsReadingFrequencyGetContent;
}

/** Conteúdo de PUT /private/app/member/business_news_reading_frequency.json */
export interface BusinessNewsReadingFrequencyPutContent extends BusinessNewsReadingFrequencyGetContent {
  completed_profile_flag: boolean;
  profile_completion_percent: number;
}

export interface BusinessNewsReadingFrequencyPutResponse {
  status: string;
  message: string;
  content: BusinessNewsReadingFrequencyPutContent;
}

/** Body de PUT /private/app/member/business_news_reading_frequency.json */
export interface BusinessNewsReadingFrequencyPutPayload {
  business_news_reading_frequency: BusinessNewsReadingFrequencySlug;
}

/** Body de POST /private/app/member/change_personal_interests.json */
export interface ChangePersonalInterestsPayload {
  interests_id: number[];
}

/** Body de PUT /private/app/member/change_password */
export interface ChangePasswordPayload {
  current_password: string;
  new_password: string;
  repeat_new_password: string;
}

/** Resposta de troca de senha — `content` pode vir vazio em sucesso. */
export interface ChangePasswordResponse {
  status: string;
  message: string;
  content?: unknown;
}

/** Membro retornado por GET /private/app/member/list */
export interface MemberListItem {
  public_id: string;
  name: string;
  professional_activity: string | null;
  /** Lista costuma usar `{ url }`; listagens de follow podem usar URL direta. */
  avatar: string | { url: string } | null;
  /** Preferir em cards/listas (menor peso). */
  avatar_thumb?: string | { url: string } | null;
  redal_plus?: boolean;
  /** Viewer autenticado segue este membro. */
  is_following?: boolean;
}

/** Mesma forma usada em GET following / followers. */
export type MemberRelationListItem = MemberListItem;

export interface FollowActionContent {
  member_id: string;
  is_following: boolean;
}

export interface FollowActionResponse {
  status: string;
  message: string;
  content: FollowActionContent;
}

export interface MemberFollowRelationsContent {
  members: MemberRelationListItem[];
  pagination: MemberListPagination;
}

export interface MemberFollowRelationsResponse {
  status: string;
  message: string;
  content: MemberFollowRelationsContent;
}

export interface MemberListPagination {
  page: number;
  per_page: number;
  total_pages: number;
  total_items: number;
}

export interface MemberListContent {
  members: MemberListItem[];
  pagination: MemberListPagination;
}

export interface MemberListResponse {
  status: string;
  message: string;
  content: MemberListContent;
}

/** Item de GET /private/app/member/get_categories — categorias para selects e cadastro. */
export interface MemberCategoryOption {
  id: number;
  name: string;
  label_category?: string | null;
}

export interface GetCategoriesResponse {
  status: string;
  message: string;
  content?: {
    categories: MemberCategoryOption[];
  };
}

// --- Objetivos de networking (GET get_networking_objectives / PUT change_networking_objectives) ---

/** Item do catálogo de objetivos de networking, já marcando a seleção do membro. */
export interface NetworkingObjectiveOption {
  id: number;
  name: string;
  description: string | null;
  position: number;
  /** `true` na opção "Outro" → exibir input de texto livre. */
  is_other: boolean;
  /** `true` se o membro já marcou esta opção. */
  selected: boolean;
  /** Texto do membro para a opção "Outro". */
  custom_value: string | null;
}

export interface GetNetworkingObjectivesResponse {
  status: string;
  message: string;
  content?: {
    networking_objectives: NetworkingObjectiveOption[];
  };
  type?: string;
  code?: number;
}

/** Body de PUT /private/app/member/change_networking_objectives.json */
export interface ChangeNetworkingObjectivesPayload {
  objective_ids: number[];
  /** Texto livre; só é gravado quando o id de "Outro" está em `objective_ids`. */
  other_value?: string;
}

/** Objetivo de networking resumido (GET info / detail_member). */
export interface MemberNetworkingObjective {
  id: number;
  name: string;
  custom_value: string | null;
}

// --- Áreas de informação e treinamento (GET get_training_information_areas / PUT change_training_information_areas) ---

/** Item do catálogo de áreas de informação e treinamento, já marcando a seleção do membro. */
export interface TrainingInformationAreaOption {
  id: number;
  name: string;
  description: string | null;
  position: number;
  /** `true` na opção "Outro" → exibir input de texto livre. */
  is_other: boolean;
  /** `true` se o membro já marcou esta opção. */
  selected: boolean;
  /** Texto do membro para a opção "Outro". */
  custom_value: string | null;
}

export interface GetTrainingInformationAreasResponse {
  status: string;
  message: string;
  content?: {
    training_information_areas: TrainingInformationAreaOption[];
  };
  type?: string;
  code?: number;
}

/** Body de PUT /private/app/member/change_training_information_areas.json */
export interface ChangeTrainingInformationAreasPayload {
  area_ids: number[];
  /** Texto livre; só é gravado quando o id de "Outro" está em `area_ids`. */
  other_value?: string;
}

/** Área de informação e treinamento resumida (GET info / detail_member). */
export interface MemberTrainingInformationArea {
  id: number;
  name: string;
  custom_value: string | null;
}

// --- Tipos de eventos de interesse (GET get_event_participation_types / PUT change_event_participation_types) ---

/** Item do catálogo de tipos de eventos de interesse, já marcando a seleção do membro. */
export interface EventParticipationTypeOption {
  id: number;
  name: string;
  description: string | null;
  position: number;
  /** `true` na opção "Outro" → exibir input de texto livre. */
  is_other: boolean;
  /** `true` se o membro já marcou esta opção. */
  selected: boolean;
  /** Texto do membro para a opção "Outro". */
  custom_value: string | null;
}

export interface GetEventParticipationTypesResponse {
  status: string;
  message: string;
  content?: {
    event_participation_types: EventParticipationTypeOption[];
  };
  type?: string;
  code?: number;
}

/** Body de PUT /private/app/member/change_event_participation_types.json */
export interface ChangeEventParticipationTypesPayload {
  type_ids: number[];
  /** Texto livre; só é gravado quando o id de "Outro" está em `type_ids`. */
  other_value?: string;
}

/** Tipo de evento de interesse resumido (GET info / detail_member). */
export interface MemberEventParticipationType {
  id: number;
  name: string;
  custom_value: string | null;
}

// --- Preferências de conteúdo (GET get_content_preferences / PUT change_content_preferences) ---

/** Item do catálogo de preferências de conteúdo, já marcando a seleção do membro. */
export interface ContentPreferenceOption {
  id: number;
  name: string;
  description: string | null;
  position: number;
  /** `true` na opção "Outro" → exibir input de texto livre. */
  is_other: boolean;
  /** `true` se o membro já marcou esta opção. */
  selected: boolean;
  /** Texto do membro para a opção "Outro". */
  custom_value: string | null;
}

export interface GetContentPreferencesResponse {
  status: string;
  message: string;
  content?: {
    content_preferences: ContentPreferenceOption[];
  };
  type?: string;
  code?: number;
}

/** Body de PUT /private/app/member/change_content_preferences.json */
export interface ChangeContentPreferencesPayload {
  preference_ids: number[];
  /** Texto livre; só é gravado quando o id de "Outro" está em `preference_ids`. */
  other_value?: string;
}

/** Preferência de conteúdo resumida (GET info / detail_member). */
export interface MemberContentPreference {
  id: number;
  name: string;
  custom_value: string | null;
}

// --- GET /private/app/member/detail_member/:id.json ---

/** Sócio listado em uma empresa (detalhe de membro). */
export interface MemberDetailCompanyPartner {
  member_name: string | null;
  /** API: em geral `{ url: string }`; pode vir como URL string. */
  member_avatar: string | { url: string | null } | null;
  position: string | null;
}

export interface MemberDetailCompanyCategory {
  name: string;
  description: string | null;
  label_category?: string | null;
}

export interface MemberDetailCompanyImage {
  /** GET `detail_member`: string ou `{ url }` (mesmo padrão do avatar / member/info). */
  url?: string | { url?: string | null } | null;
  image_url?: string | { url?: string | null } | null;
  position: number;
  description: string | null;
}

export interface MemberDetailCompany {
  id: string;
  logo: string | null;
  cnpj: string | null;
  legal_name: string | null;
  trade_name: string | null;
  /** HTML permitido (mesmo formato do perfil / edição da empresa). */
  description?: string | null;
  company_instagram?: string | null;
  website?: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  country: string | null;
  phone: string | null;
  annual_revenue_string: string | null;
  number_employees_string: string | null;
  annual_revenue_flag?: boolean | null;
  number_employees_flag?: boolean | null;
  created_at: string;
  updated_at: string;
  images: MemberDetailCompanyImage[];
  members: MemberDetailCompanyPartner[];
  categories: MemberDetailCompanyCategory[];
}

export interface MemberDetailInterestItem {
  name: string;
  description: string | null;
  type: string | null;
}

/** Evento aninhado no detalhe do membro (mesma família do get_detail de eventos). */
export interface MemberDetailEventItem {
  id: string;
  theme: string | null;
  start_datetime: string | null;
  end_datetime: string | null;
  speaker_name: string | null;
  description: string | null;
  location: string | null;
  cover_image: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  event_images: EventDetailApiImage[];
  event_tickets: EventDetailApiTicket[];
  event_registrations: EventDetailApiRegistration[];
  event_schedules: EventDetailApiSchedule[];
}

export interface MemberDetailContent {
  id: string;
  name: string | null;
  professional_activity: string | null;
  average_annual_revenue: string | number | null;
  employees_count: number | null;
  industry_sector: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  country: string | null;
  birth_date: string | null;
  gender: string | null;
  /** API `detail_member`: em geral `{ url: string }`; pode vir como string direta. */
  avatar: { url: string | null } | string | null;
  instagram: string | null;
  description: string | null;
  goal_redal: string | null;
  created_at: string;
  show_phone: boolean;
  show_email: boolean;
  email?: string | null;
  phone?: string | null;
  cpf?: string | null;
  interests: MemberDetailInterestItem[];
  content_preferences?: MemberContentPreference[];
  networking_objectives?: MemberNetworkingObjective[];
  training_information_areas?: MemberTrainingInformationArea[];
  event_participation_types?: MemberEventParticipationType[];
  companies: MemberDetailCompany[];
  events: MemberDetailEventItem[];
  is_following?: boolean;
  followers_count?: number;
  following_count?: number;
}

export interface MemberDetailResponse {
  status: string;
  message: string;
  content?: MemberDetailContent;
  type?: string;
  code?: number;
}

// --- GET /private/app/member/my_events.json ---

/** Item em `content.events` — eventos em que o membro tem vínculo via `member_payments`. */
export interface MemberMyEventItem {
  event_id: string;
  theme: string;
  start_datetime: string;
  end_datetime: string;
  cover_image: string | null;
  location: string | null;
  city: string | null;
  state: string | null;
  speaker_name: string | null;
  /** `true` = ainda aberto (pode abrir detalhe); `false` = data já passou. */
  is_accessible: boolean;
  /** Pagamento confirmado (RECEIVED, CONFIRMED, RECEIVED_IN_CASH, etc.). */
  is_paid: boolean;
  payment_status: string;
  invoice_url: string | null;
  invoice_pdf_url: string | null;
  invoice_xml_url: string | null;
  invoice_status: string | null;
  event_ticket_price: string | null;
  event_ticket_batch: string | null;
  payment_created_at: string | null;
}

export interface MemberMyEventsContent {
  events: MemberMyEventItem[];
}

export interface MemberMyEventsResponse {
  status: string;
  message: string;
  content?: MemberMyEventsContent;
  type?: string;
  code?: number;
}

@Injectable({ providedIn: 'root' })
export class MemberService {
  private readonly apiUrl = environment.apiUrl;

  /** Cache em memória (lista muda pouco; invalida ao recarregar a página). */
  private categoriesList$: Observable<GetCategoriesResponse> | null = null;

  /** Catálogo de interesses (GET get_interests). */
  private interestsCatalog$: Observable<GetInterestsResponse> | null = null;

  constructor(private http: HttpClient) {}

  getInfo(): Observable<MemberInfoResponse> {
    return this.http.get<MemberInfoResponse>(`${this.apiUrl}/private/app/member/info`);
  }

  /**
   * Atualiza dados pessoais do membro autenticado.
   * POST /private/app/member/change_personal_data.json
   */
  updatePersonalData(body: MemberPersonalDataUpdatePayload): Observable<ChangePersonalDataResponse> {
    return this.http.post<ChangePersonalDataResponse>(
      `${this.apiUrl}/private/app/member/change_personal_data.json`,
      body
    );
  }

  /**
   * Atualiza o logo da empresa (multipart). `companyId` é o public_id da empresa.
   * POST /private/app/member/change_company_logo.json
   */
  changeCompanyLogo(companyId: string, logoFile: File): Observable<ChangeCompanyLogoResponse> {
    return this.changeCompanyLogoWithProgress(companyId, logoFile).pipe(
      filter((e): e is UploadProgressEvent<ChangeCompanyLogoResponse> & { type: 'response' } => e.type === 'response'),
      map((e) => e.body as ChangeCompanyLogoResponse)
    );
  }

  changeCompanyLogoWithProgress(
    companyId: string,
    logoFile: File
  ): Observable<UploadProgressEvent<ChangeCompanyLogoResponse>> {
    const formData = new FormData();
    formData.append('company_id', companyId);
    formData.append('logo', logoFile, logoFile.name);
    return postWithUploadProgress<ChangeCompanyLogoResponse>(
      this.http,
      `${this.apiUrl}/private/app/member/change_company_logo.json`,
      formData
    );
  }

  /**
   * Atualiza o avatar do membro (multipart).
   * POST /private/app/member/change_avatar.json
   */
  changeAvatar(avatarFile: File): Observable<any> {
    return this.changeAvatarWithProgress(avatarFile).pipe(
      filter((e): e is UploadProgressEvent<any> & { type: 'response' } => e.type === 'response'),
      map((e) => e.body)
    );
  }

  changeAvatarWithProgress(avatarFile: File): Observable<UploadProgressEvent<any>> {
    const formData = new FormData();
    formData.append('avatar', avatarFile, avatarFile.name);
    return postWithUploadProgress<any>(
      this.http,
      `${this.apiUrl}/private/app/member/change_avatar.json`,
      formData
    );
  }

  /**
   * Atualiza dados cadastrais da empresa (membro deve ser sócio). `company_id` = public_id.
   * POST /private/app/member/change_company_date.json
   */
  changeCompanyData(body: MemberChangeCompanyDataPayload): Observable<ChangePersonalDataResponse> {
    return this.http.post<ChangePersonalDataResponse>(
      `${this.apiUrl}/private/app/member/change_company_date.json`,
      body
    );
  }

  /**
   * Dados profissionais do membro + faixas/categorias da empresa (membro deve ser sócio).
   * POST /private/app/member/change_professional_data.json
   */
  changeProfessionalData(
    body: ChangeProfessionalDataPayload
  ): Observable<ChangePersonalDataResponse> {
    return this.http.post<ChangePersonalDataResponse>(
      `${this.apiUrl}/private/app/member/change_professional_data.json`,
      body
    );
  }

  /**
   * Atualiza objetivo Redal (`goal_redal`) e/ou texto “sobre mim” (`description`).
   * POST /private/app/member/change_goal_redal.json — pelo menos um campo no body.
   */
  updateObjectivesData(body: MemberObjectivesUpdatePayload): Observable<ChangePersonalDataResponse> {
    return this.http.post<ChangePersonalDataResponse>(
      `${this.apiUrl}/private/app/member/change_goal_redal.json`,
      body
    );
  }

  /**
   * Lê sugestões de temas e palestrantes para eventos do membro autenticado.
   * GET /private/app/member/event_theme_speaker_suggestions.json
   */
  getEventThemeSpeakerSuggestions(): Observable<EventThemeSpeakerSuggestionsGetResponse> {
    return this.http.get<EventThemeSpeakerSuggestionsGetResponse>(
      `${this.apiUrl}/private/app/member/event_theme_speaker_suggestions.json`
    );
  }

  /**
   * Atualiza sugestões de temas e palestrantes para eventos (`""` limpa o campo).
   * PUT /private/app/member/event_theme_speaker_suggestions.json
   */
  updateEventThemeSpeakerSuggestions(
    body: EventThemeSpeakerSuggestionsPutPayload
  ): Observable<EventThemeSpeakerSuggestionsPutResponse> {
    return this.http.put<EventThemeSpeakerSuggestionsPutResponse>(
      `${this.apiUrl}/private/app/member/event_theme_speaker_suggestions.json`,
      body
    );
  }

  /**
   * Lê a avaliação do cenário econômico do membro autenticado.
   * GET /private/app/member/economic_scenario_rating.json
   */
  getEconomicScenarioRating(): Observable<EconomicScenarioRatingGetResponse> {
    return this.http.get<EconomicScenarioRatingGetResponse>(
      `${this.apiUrl}/private/app/member/economic_scenario_rating.json`
    );
  }

  /**
   * Atualiza a avaliação do cenário econômico (inteiro de 1 a 5).
   * PUT /private/app/member/economic_scenario_rating.json
   */
  updateEconomicScenarioRating(
    body: EconomicScenarioRatingPutPayload
  ): Observable<EconomicScenarioRatingPutResponse> {
    return this.http.put<EconomicScenarioRatingPutResponse>(
      `${this.apiUrl}/private/app/member/economic_scenario_rating.json`,
      body
    );
  }

  /**
   * Lê a frequência de leitura de notícias de negócios do membro autenticado.
   * GET /private/app/member/business_news_reading_frequency.json
   */
  getBusinessNewsReadingFrequency(): Observable<BusinessNewsReadingFrequencyGetResponse> {
    return this.http.get<BusinessNewsReadingFrequencyGetResponse>(
      `${this.apiUrl}/private/app/member/business_news_reading_frequency.json`
    );
  }

  /**
   * Atualiza a frequência de leitura de notícias de negócios.
   * PUT /private/app/member/business_news_reading_frequency.json
   */
  updateBusinessNewsReadingFrequency(
    body: BusinessNewsReadingFrequencyPutPayload
  ): Observable<BusinessNewsReadingFrequencyPutResponse> {
    return this.http.put<BusinessNewsReadingFrequencyPutResponse>(
      `${this.apiUrl}/private/app/member/business_news_reading_frequency.json`,
      body
    );
  }

  /**
   * Substitui os interesses pessoais do membro (IDs do catálogo `get_interests`).
   * POST /private/app/member/change_personal_interests.json
   */
  changePersonalInterests(
    body: ChangePersonalInterestsPayload
  ): Observable<ChangePersonalDataResponse> {
    return this.http.post<ChangePersonalDataResponse>(
      `${this.apiUrl}/private/app/member/change_personal_interests.json`,
      body
    );
  }

  /**
   * Catálogo ativo de objetivos de networking (ordenado por `position`) já marcando o que o membro selecionou.
   * GET /private/app/member/get_networking_objectives.json
   */
  getNetworkingObjectives(): Observable<GetNetworkingObjectivesResponse> {
    return this.http.get<GetNetworkingObjectivesResponse>(
      `${this.apiUrl}/private/app/member/get_networking_objectives.json`
    );
  }

  /**
   * Substitui o conjunto de objetivos de networking do membro pelo array enviado.
   * `other_value` só é gravado quando o id de "Outro" está em `objective_ids`.
   * PUT /private/app/member/change_networking_objectives.json
   */
  changeNetworkingObjectives(
    body: ChangeNetworkingObjectivesPayload
  ): Observable<ChangePersonalDataResponse> {
    return this.http.put<ChangePersonalDataResponse>(
      `${this.apiUrl}/private/app/member/change_networking_objectives.json`,
      body
    );
  }

  /**
   * Catálogo ativo de áreas de informação e treinamento (ordenado por `position`) já marcando o que o membro selecionou.
   * GET /private/app/member/get_training_information_areas.json
   */
  getTrainingInformationAreas(): Observable<GetTrainingInformationAreasResponse> {
    return this.http.get<GetTrainingInformationAreasResponse>(
      `${this.apiUrl}/private/app/member/get_training_information_areas.json`
    );
  }

  /**
   * Substitui o conjunto de áreas de informação e treinamento do membro pelo array enviado.
   * `other_value` só é gravado quando o id de "Outro" está em `area_ids`.
   * PUT /private/app/member/change_training_information_areas.json
   */
  changeTrainingInformationAreas(
    body: ChangeTrainingInformationAreasPayload
  ): Observable<ChangePersonalDataResponse> {
    return this.http.put<ChangePersonalDataResponse>(
      `${this.apiUrl}/private/app/member/change_training_information_areas.json`,
      body
    );
  }

  /**
   * Catálogo ativo de tipos de eventos de interesse (ordenado por `position`) já marcando o que o membro selecionou.
   * GET /private/app/member/get_event_participation_types.json
   */
  getEventParticipationTypes(): Observable<GetEventParticipationTypesResponse> {
    return this.http.get<GetEventParticipationTypesResponse>(
      `${this.apiUrl}/private/app/member/get_event_participation_types.json`
    );
  }

  /**
   * Substitui o conjunto de tipos de eventos de interesse do membro pelo array enviado.
   * `other_value` só é gravado quando o id de "Outro" está em `type_ids`.
   * PUT /private/app/member/change_event_participation_types.json
   */
  changeEventParticipationTypes(
    body: ChangeEventParticipationTypesPayload
  ): Observable<ChangePersonalDataResponse> {
    return this.http.put<ChangePersonalDataResponse>(
      `${this.apiUrl}/private/app/member/change_event_participation_types.json`,
      body
    );
  }

  /**
   * Catálogo ativo de preferências de conteúdo (ordenado por `position`) já marcando o que o membro selecionou.
   * GET /private/app/member/get_content_preferences.json
   */
  getContentPreferences(): Observable<GetContentPreferencesResponse> {
    return this.http.get<GetContentPreferencesResponse>(
      `${this.apiUrl}/private/app/member/get_content_preferences.json`
    );
  }

  /**
   * Substitui o conjunto de preferências de conteúdo do membro pelo array enviado.
   * `other_value` só é gravado quando o id de "Outro" está em `preference_ids`.
   * PUT /private/app/member/change_content_preferences.json
   */
  changeContentPreferences(
    body: ChangeContentPreferencesPayload
  ): Observable<ChangePersonalDataResponse> {
    return this.http.put<ChangePersonalDataResponse>(
      `${this.apiUrl}/private/app/member/change_content_preferences.json`,
      body
    );
  }

  /**
   * Altera a senha do membro autenticado. Após sucesso, tokens antigos deixam de valer — exigir novo login.
   * PUT /private/app/member/change_password.json
   */
  changePassword(body: ChangePasswordPayload): Observable<ChangePasswordResponse> {
    return this.http.put<ChangePasswordResponse>(
      `${this.apiUrl}/private/app/member/change_password.json`,
      body
    );
  }

  /**
   * Retorna lista paginada de membros.
   * Rota: GET /private/app/member/list.json
   * Auth: Bearer token (injetado automaticamente pelo interceptor).
   */
  list(page = 1, perPage = 12): Observable<MemberListResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('per_page', perPage.toString());

    return this.http.get<MemberListResponse>(
      `${this.apiUrl}/private/app/member/list.json`,
      { params }
    );
  }

  /**
   * Lista completa de categorias (setores), ordenada no servidor por nome.
   * GET /private/app/member/get_categories.json
   */
  getCategories(): Observable<GetCategoriesResponse> {
    if (!this.categoriesList$) {
      this.categoriesList$ = this.http
        .get<GetCategoriesResponse>(`${this.apiUrl}/private/app/member/get_categories.json`)
        .pipe(shareReplay({ bufferSize: 1, refCount: false }));
    }
    return this.categoriesList$;
  }

  /**
   * Catálogo global de interesses, agrupados por tipo.
   * GET /private/app/member/get_interests.json
   */
  getInterests(): Observable<GetInterestsResponse> {
    if (!this.interestsCatalog$) {
      this.interestsCatalog$ = this.http
        .get<GetInterestsResponse>(`${this.apiUrl}/private/app/member/get_interests.json`)
        .pipe(shareReplay({ bufferSize: 1, refCount: false }));
    }
    return this.interestsCatalog$;
  }

  /**
   * Perfil completo de um membro (interesses, empresas, eventos).
   * GET /private/app/member/detail_member/:id.json — `id` = public_id.
   */
  detailMember(publicId: string): Observable<MemberDetailResponse> {
    const id = encodeURIComponent(publicId);
    return this.http.get<MemberDetailResponse>(
      `${this.apiUrl}/private/app/member/detail_member/${id}.json`
    );
  }

  /**
   * Seguir outro membro (relação unidirecional).
   * POST /private/app/member/follow.json
   */
  followMember(memberId: string): Observable<FollowActionResponse> {
    return this.http.post<FollowActionResponse>(`${this.apiUrl}/private/app/member/follow.json`, {
      member_id: memberId,
    });
  }

  /**
   * Deixar de seguir.
   * DELETE /private/app/member/unfollow.json
   */
  unfollowMember(memberId: string): Observable<FollowActionResponse> {
    return this.http.delete<FollowActionResponse>(
      `${this.apiUrl}/private/app/member/unfollow.json`,
      { body: { member_id: memberId } }
    );
  }

  /**
   * Membros que o viewer segue.
   * GET /private/app/member/following.json
   */
  listFollowing(page = 1, perPage = 10): Observable<MemberFollowRelationsResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('per_page', perPage.toString());
    return this.http.get<MemberFollowRelationsResponse>(
      `${this.apiUrl}/private/app/member/following.json`,
      { params }
    );
  }

  /**
   * Membros que seguem o viewer.
   * GET /private/app/member/followers.json
   */
  listFollowers(page = 1, perPage = 10): Observable<MemberFollowRelationsResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('per_page', perPage.toString());
    return this.http.get<MemberFollowRelationsResponse>(
      `${this.apiUrl}/private/app/member/followers.json`,
      { params }
    );
  }

  /**
   * Eventos em que o membro autenticado participa (via `member_payments`).
   * GET /private/app/member/my_events.json
   */
  getMyEvents(): Observable<MemberMyEventsResponse> {
    return this.http.get<MemberMyEventsResponse>(
      `${this.apiUrl}/private/app/member/my_events.json`
    );
  }

  /**
   * Altera o cargo (position) de um membro na empresa.
   * PUT /private/app/member/change_member_position.json
   */
  changeMemberPosition(body: ChangeMemberPositionPayload): Observable<ChangeMemberPositionResponse> {
    return this.http.put<ChangeMemberPositionResponse>(
      `${this.apiUrl}/private/app/member/change_member_position.json`,
      body
    );
  }
}
